import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl border-b border-charcoal/10 py-3"
            : "bg-transparent border-b border-transparent py-6"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("#hero")}
            className={`font-serif text-2xl tracking-tight transition-colors duration-500 ${
              scrolled ? "text-charcoal" : "text-cream"
            }`}
          >
            Vistara
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className={`text-sm font-sans tracking-wide relative group transition-colors duration-500 ${
                  scrolled ? "text-charcoal/80 hover:text-terracotta" : "text-cream/90 hover:text-white"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-terracotta transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <button
            data-testid="nav-book-btn"
            onClick={() => go("#booking")}
            className={`hidden md:inline-flex items-center px-6 py-2.5 rounded-sm text-sm tracking-wide transition-all duration-300 hover:scale-[1.04] ${
              scrolled
                ? "bg-terracotta text-cream hover:brightness-110"
                : "bg-cream/15 text-cream border border-cream/40 backdrop-blur-md hover:bg-cream hover:text-charcoal"
            }`}
          >
            Reserve a stay
          </button>

          <button
            data-testid="nav-hamburger"
            onClick={() => setOpen(true)}
            className={`md:hidden ${scrolled ? "text-charcoal" : "text-cream"}`}
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[110] bg-charcoal text-cream flex flex-col"
          >
            <div className="flex justify-between items-center px-6 py-6 border-b border-cream/10">
              <span className="font-serif text-2xl">Vistara</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col justify-center flex-1 px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-5xl py-2 text-cream/90 hover:text-amber transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="mobile-book-btn"
                onClick={() => go("#booking")}
                className="mt-8 px-6 py-4 bg-terracotta text-cream rounded-sm text-sm tracking-widest uppercase"
              >
                Reserve a stay
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
